import { useState } from "react";
import { Plus } from "lucide-react";
import { SortableContext, rectSortingStrategy } from "@dnd-kit/sortable";
import { useDroppable } from "@dnd-kit/core";
import type { Card } from "../lib/types";
import { CardItem } from "./CardItem";

type Props = {
  cards: Card[];
  threadAccent: Card["accent"] extends infer A ? Exclude<A, null> : never;
  onAddCard: (title: string) => void;
  onCardClick: (card: Card) => void;
};

export function FlatCanvas({ cards, threadAccent, onAddCard, onCardClick }: Props) {
  const [adding, setAdding] = useState(false);
  const [newTitle, setNewTitle] = useState("");

  const { setNodeRef, isOver } = useDroppable({
    id: "flat-drop",
    data: { type: "flat" },
  });

  return (
    <div ref={setNodeRef} className="px-8 pt-4 pb-12">
      <SortableContext items={cards.map((c) => c.id)} strategy={rectSortingStrategy}>
        {/*
         * items-start prevents grid auto-stretch: by default CSS Grid
         * makes every cell in a row the height of the tallest cell, so
         * expanding one card visually grew its neighbours too. With
         * items-start each card sits at the top of its grid cell at its
         * own natural height, and shorter cards no longer get pulled up.
         */}
        <div className="grid grid-cols-1 items-start gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {cards.map((c) => (
            <CardItem
              key={c.id}
              card={c}
              fallbackAccent={threadAccent as any}
              onClick={() => onCardClick(c)}
            />
          ))}

          {adding ? (
            <div className="rounded-card border border-border bg-surface p-2 shadow-card">
              <input
                autoFocus
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && newTitle.trim()) {
                    onAddCard(newTitle.trim());
                    setNewTitle("");
                  } else if (e.key === "Escape") {
                    setAdding(false);
                    setNewTitle("");
                  }
                }}
                onBlur={() => {
                  if (newTitle.trim()) {
                    onAddCard(newTitle.trim());
                    setNewTitle("");
                  }
                  setAdding(false);
                }}
                placeholder="what's on your mind?"
                className="input-bare w-full px-2 py-2 text-sm"
              />
            </div>
          ) : (
            <button
              onClick={() => setAdding(true)}
              className="flex h-full min-h-[64px] items-center justify-center gap-1.5 rounded-card border border-dashed border-border-strong/60 px-3 py-3 text-xs text-ink-mute transition-colors hover:border-lavender-200 hover:bg-lavender-50/30 hover:text-ink-soft"
            >
              <Plus size={12} /> add card
            </button>
          )}
        </div>
      </SortableContext>
    </div>
  );
}
